import axios from "axios";

// Prefer same-origin `/api` in production (Emergent ingress routes /api/* to
// backend on every domain — preview, *.emergent.host, and custom domains like
// fanpuff.com). This avoids cross-origin CORS issues where Cloudflare strips
// `allow-credentials`. Dev fallback: if the current origin doesn't have /api
// routing, the env var is used (e.g. running CRA on localhost:3000).
const sameOrigin = typeof window !== "undefined" ? window.location.origin : "";
const envBackend = process.env.REACT_APP_BACKEND_URL || "";
// Use same-origin when we're deployed (HTTPS) — else fall back to env var for local dev
const BACKEND = sameOrigin && sameOrigin.startsWith("https://") ? sameOrigin : envBackend;

export const API = `${BACKEND}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Auth
export const getMe = () => api.get("/auth/me").then(r => r.data);
export const exchangeSession = (session_id) => api.post("/auth/session", { session_id }).then(r => r.data);
export const logoutApi = () => api.post("/auth/logout").then(r => r.data);
export const setupAccount = (p) => api.post("/auth/setup", p).then(r => r.data);
export const registerApi = (p) => api.post("/auth/register", p).then(r => r.data);
export const loginApi = (p) => api.post("/auth/login", p).then(r => r.data);

// Trend indexes
export const getTrendIndexes = (params = {}) => api.get("/trend-indexes", { params }).then(r => r.data);
export const getTrendIndex = (id) => api.get(`/trend-indexes/${id}`).then(r => r.data);

// Contests
export const getContests = (params = {}) => api.get("/contests", { params }).then(r => r.data);
export const getContest = (id) => api.get(`/contests/${id}`).then(r => r.data);
export const getLeaderboard = (id) => api.get(`/contests/${id}/leaderboard`).then(r => r.data);
export const joinContest = (id, payload) => api.post(`/contests/${id}/join`, payload).then(r => r.data);

// Lineups + Market
export const getMyLineups = () => api.get("/lineups").then(r => r.data);
export const getHoldings = () => api.get("/market/holdings").then(r => r.data);
export const tradeIndex = (p) => api.post("/market/trade", p).then(r => r.data);
export const getTrades = () => api.get("/market/trades").then(r => r.data);

// Feed
export const getFeed = () => api.get("/feed").then(r => r.data);

// Profile
export const updateProfile = (p) => api.patch("/profile", p).then(r => r.data);
export const getProfileStats = () => api.get("/profile/stats").then(r => r.data);

// Economy
export const getStatus = () => api.get("/me/status").then(r => r.data);
export const claimDaily = () => api.post("/daily/claim").then(r => r.data);
export const claimMissions = () => api.post("/missions/claim").then(r => r.data);
export const tickMission = (id) => api.post(`/missions/tick/${id}`).then(r => r.data);
export const getMilestones = () => api.get("/milestones").then(r => r.data);
export const getVault = () => api.get("/vault").then(r => r.data);
export const claimMilestone = (mid) => api.post(`/vault/claim/${mid}`).then(r => r.data);
export const getWalletStatus = () => api.get("/me/wallet-status").then(r => r.data);
export const claimRecoveryPack = () => api.post("/recovery-pack/claim").then(r => r.data);
export const claimVault = (mid) => api.post(`/vault/claim/${mid}`).then(r => r.data);
export const getGlobalLeaderboard = (type = "xp") => api.get("/leaderboard", { params: { type } }).then(r => r.data);

// Social
export const followUser = (id) => api.post(`/users/${id}/follow`).then(r => r.data);
export const unfollowUser = (id) => api.delete(`/users/${id}/follow`).then(r => r.data);
export const getPublicUser = (id) => api.get(`/users/${id}`).then(r => r.data);
export const compareLineups = (a, b) => api.get("/compare", { params: { a, b } }).then(r => r.data);

// Admin
export const getAdminConfig = () => api.get("/admin/config").then(r => r.data);
export const updateAdminConfig = (p) => api.patch("/admin/config", p).then(r => r.data);
export const applyAdminRecommendation = (p = {}) => api.patch("/admin/config", { ...p, apply_recommendation: true }).then(r => r.data);
export const getAdminOverview = () => api.get("/admin/overview").then(r => r.data);
export const adminSettleAll = () => api.post("/admin/contests/settle").then(r => r.data);
export const adminSettleOne = (cid) => api.post(`/admin/contests/${cid}/settle`).then(r => r.data);

// Notifications
export const getNotifications = () => api.get("/notifications").then(r => r.data);
export const readAll = () => api.post("/notifications/read-all").then(r => r.data);

// Influencer Requests
export const listInfluencerRequests = (status = "pending") => api.get("/influencer-requests", { params: { status } }).then(r => r.data);
export const submitInfluencerRequest = (p) => api.post("/influencer-requests", p).then(r => r.data);
export const voteInfluencerRequest = (rid) => api.post(`/influencer-requests/${rid}/vote`).then(r => r.data);
export const adminUpdateInfluencerRequest = (rid, status) => api.patch(`/admin/influencer-requests/${rid}`, { status }).then(r => r.data);

// Duels (Friend Challenges)
export const createDuel = (p) => api.post("/duels", p).then(r => r.data);
export const getDuelByCode = (code) => api.get(`/duels/code/${code}`).then(r => r.data);
export const acceptDuel = (code, p) => api.post(`/duels/code/${code}/accept`, p).then(r => r.data);
export const getMyDuels = () => api.get("/duels/mine").then(r => r.data);
